import React from "react";
import { Link } from "react-router-dom";
import LegalDocumentPage from "@/components/LegalDocumentPage";

const LegalList = ({ children }) => <ul className="ml-5 list-disc space-y-2">{children}</ul>;

const PRIVACY_SECTIONS = [
  { heading: "Effective Date", content: "August 2, 2026" },
  {
    heading: "Who We Are and Scope",
    content: (
      <>
        This Privacy Policy explains how Emerging Technology LLC, doing business as ShowMeLive
        ("ShowMeLive," "we," "us," or "our"), collects, uses, discloses, retains, and protects
        personal information when you use showmelive.app, our mobile applications, livestreams,
        creator tools, and related services (collectively, the "Services").
      </>
    ),
  },
  {
    heading: "Information We Collect",
    content: (
      <LegalList>
        <li><strong>Account and profile information:</strong> name, username, profile image, role, account settings, creator information, and information you add to a public profile.</li>
        <li><strong>Email and social-login identifiers:</strong> email address; Google, Apple, Facebook, Instagram, or other provider identifiers; authentication tokens; linked-account status; and limited profile information authorized by the provider. We do not receive your social-login password.</li>
        <li><strong>Camera and microphone information:</strong> permission status and live audio/video tracks when a creator broadcasts or connects a production device. Viewers do not publish camera or microphone tracks unless a feature clearly asks for permission.</li>
        <li><strong>Photos and uploaded media:</strong> profile images, event artwork, promotional images, camera-device media, and other files you choose to upload.</li>
        <li><strong>Live video, audio, chat, and engagement:</strong> livestream content, event attendance, chat messages, questions, reactions, tips, moderation actions, and timestamps.</li>
        <li><strong>Location and geofencing:</strong> event venue, city, state, and coordinates supplied by creators; and a viewer's approximate or precise device location when the viewer chooses to use nearby search or access a geofenced event. Viewer coordinates are used to perform the requested distance check and are not added to the viewer's public profile.</li>
        <li><strong>Purchases and transaction records:</strong> event, ticket, tip, refund, dispute, payout, amount, currency, status, transaction identifier, and limited billing or customer information provided by the payment processor. We do not store full payment-card numbers.</li>
        <li><strong>Logs, device data, and IP addresses:</strong> IP address, browser, operating system, device identifiers, app version, request and error logs, session information, connection quality, and diagnostic events.</li>
        <li><strong>Security and anti-capture records:</strong> detectable screenshot or print shortcuts, screen-share calls, extended tab hiding, suspected recording signals, device and session information, warnings, event bans, suspensions, and related enforcement records. A website cannot reliably detect every operating-system screenshot or recording, so these records describe signals detected by the Services, not proof of every capture.</li>
        <li><strong>Notifications:</strong> notification preferences, in-app and email messages, delivery status, and a device push token if mobile push notifications are enabled.</li>
        <li><strong>Communications:</strong> support requests, legal notices, account-deletion requests, reports, feedback, and other correspondence.</li>
      </LegalList>
    ),
  },
  {
    heading: "How We Use Information",
    content: (
      <LegalList>
        <li>Provide accounts, social sign-in, profiles, event discovery, tickets, livestreams, chat, creator tools, payments, payouts, and customer support.</li>
        <li>Request and use camera, microphone, photo-library, or location access only when needed for the feature you select and subject to device permissions.</li>
        <li>Authenticate users, maintain sessions, secure content, detect abuse, investigate reports, enforce event bans and platform rules, and prevent fraud.</li>
        <li>Process purchases, refunds, disputes, taxes, accounting, and creator payouts.</li>
        <li>Send service, transaction, moderation, security, event, and optional promotional notifications according to your settings and applicable law.</li>
        <li>Monitor reliability, diagnose failures, improve performance and features, comply with law, establish or defend legal claims, and protect users and the public.</li>
      </LegalList>
    ),
  },
  {
    id: "reports-moderation-and-safety-records",
    heading: "Reports, Moderation, and Safety Records",
    content: (
      <>
        When an authenticated viewer reports a live event, we record the event, viewer account,
        creator, selected report category, timestamps, report status, and related technical or
        enforcement information. We use one report per viewer account when evaluating the automatic
        threshold. If 10 distinct viewers choose the same category during the same active livestream,
        the system automatically stops that stream as a precaution, creates moderation records, and
        emails the creator with the report category and relevant Terms and Privacy Policy sections.
        We use these records to prevent duplicate reports, investigate abuse, enforce our rules,
        identify coordinated or fraudulent reporting, resolve disputes, and protect users. Reports
        are not displayed publicly, but may be disclosed to the affected creator, administrators,
        processors, legal authorities, or others when reasonably necessary for review, safety, legal
        compliance, or defense of claims.
      </>
    ),
  },
  {
    heading: "Payment Processing",
    content: (
      <>
        Stripe processes web payments and creator payout onboarding. If a purchase is offered through
        an iOS or Android app, Apple or Google may process the purchase under its own account and
        privacy terms. Those processors may collect payment credentials, billing information, tax
        information, fraud signals, and device or account identifiers directly. ShowMeLive generally
        receives a transaction identifier, product, amount, status, and limited customer information
        needed to provide the purchase, handle support, and keep financial records. Apple and Google
        social sign-in are separate from Apple App Store or Google Play purchase processing.
      </>
    ),
  },
  {
    heading: "Processors and Other Disclosures",
    content: (
      <>
        We disclose information only as reasonably necessary to operate the Services, complete a
        transaction, follow your instructions, protect rights and safety, or comply with law. Our
        processors and integration partners include:
        <LegalList>
          <li><strong>Amazon Web Services (AWS), including AWS IVS:</strong> hosting, storage, content delivery, infrastructure, logs, and livestream distribution.</li>
          <li><strong>LiveKit:</strong> real-time audio/video rooms, participant connections, and related streaming diagnostics.</li>
          <li><strong>MongoDB:</strong> application database hosting and storage.</li>
          <li><strong>Stripe:</strong> payments, refunds, fraud prevention, connected-account onboarding, and payouts.</li>
          <li><strong>Apple and Google:</strong> social sign-in, mobile platform services, and app-store purchase processing when offered.</li>
          <li><strong>Meta services, including Facebook and Instagram:</strong> social sign-in and linked-account features when selected.</li>
          <li><strong>Email, notification, mapping/geocoding, analytics, security, and support vendors:</strong> only for the functions we enable and subject to their applicable terms.</li>
        </LegalList>
        We may also disclose information in a merger, financing, acquisition, reorganization, or sale
        of assets, subject to appropriate protections. We do not sell personal information for money.
        We do not use or disclose sensitive personal information to infer characteristics about you.
      </>
    ),
  },
  {
    heading: "Public and Creator-Visible Information",
    content: (
      <>
        Your username, profile image, public profile, creator name, event information, and content you
        intentionally publish may be visible to other users. Event creators may receive event-scoped
        attendance, transaction, engagement, moderation, and content-protection reports, including the
        identity of viewers associated with detectable anti-capture signals. Chat messages and reactions
        may be visible during an event. Do not publish information you want to keep private.
      </>
    ),
  },
  {
    heading: "Retention Periods",
    content: (
      <LegalList>
        <li><strong>OAuth state:</strong> approximately 15 minutes. Standard login sessions expire after 7 days unless ended sooner.</li>
        <li><strong>Account, profile, linked-login, notification, attendance, chat, reaction, uploaded-media, and content-protection records:</strong> generally while the account or related event remains active, then deleted or de-identified through the account or event deletion process, unless a longer period is required for safety, disputes, legal obligations, or enforcement.</li>
        <li><strong>Live media:</strong> retained only when recording, replay, moderation, backup, or another disclosed feature stores it; otherwise live transport is transient. Stored event media remains until the creator deletes it, the account is deleted, or it is removed under our policies.</li>
        <li><strong>Viewer location:</strong> used for the requested nearby-search or geofence check and not stored as a persistent viewer profile field. Creator-supplied event location remains with the event until it is changed or deleted.</li>
        <li><strong>Payment, refund, dispute, tax, fraud-prevention, and payout records:</strong> retained for the transaction lifecycle and typically up to 7 years where needed for accounting, tax, anti-fraud, contractual, or legal obligations. Direct identifiers may be replaced with a deleted-user identifier after account deletion.</li>
        <li><strong>Operational and security logs:</strong> retained for as long as reasonably necessary to secure and troubleshoot the Services, investigate abuse, enforce restrictions, and meet legal obligations. Account-linked security records are deleted during normal account deletion unless preservation is legally required.</li>
        <li><strong>Deletion receipts and backups:</strong> a hashed deletion receipt may be retained to document completion. Residual encrypted backup copies may remain until the applicable backup cycle expires and are not restored for ordinary business use.</li>
      </LegalList>
    ),
  },
  {
    heading: "Account Deletion",
    content: (
      <>
        You may review linked sign-in methods and start secure deletion at{" "}
        <Link className="font-semibold text-blue-400 hover:text-blue-300" to="/account-deletion">Account and Data Deletion</Link>.
        We require recent reauthentication. Active livestreams and unresolved payouts, refunds, or
        disputes may need to be completed first. Deletion removes account information, sessions,
        social credentials, user-generated content, uploads, notifications, and account-linked
        security records in the normal deletion workflow. Legally required transaction and
        fraud-prevention records may be retained in de-identified form. You may also contact{" "}
        <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:support@showmelive.app">support@showmelive.app</a>.
      </>
    ),
  },
  {
    heading: "Children and Age Limits",
    content: (
      <>
        The Services are not directed to children under 13, and we do not knowingly collect personal
        information from children under 13. Users ages 13 through the age of legal majority may use the
        Services only with permission and supervision from a parent or legal guardian where required.
        Content Creators must be at least 18. If you believe a child under 13 provided personal
        information, contact us so we can investigate and delete it. We may request information needed
        to verify age or parental authority.
      </>
    ),
  },
  {
    heading: "International Data Transfers",
    content: (
      <>
        ShowMeLive is operated from the United States. We and our processors may process information
        in the United States and other countries where privacy laws may differ from those where you
        live. Where required, we use recognized transfer mechanisms and contractual, technical, or
        organizational safeguards intended to protect transferred personal information.
      </>
    ),
  },
  {
    heading: "Your Privacy Rights",
    content: (
      <>
        Depending on where you live, you may have rights to request access, correction, deletion,
        portability, restriction, or objection; withdraw consent; opt out of certain processing;
        appeal a denied request; or complain to a data-protection authority. You may update some
        information in account settings and control device permissions in your operating system.
        To submit a request, email{" "}
        <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:privacy@showmelive.app">privacy@showmelive.app</a>.
        We may verify your identity and authority before responding. Authorized agents may submit
        requests where permitted by law. We will not discriminate against you for exercising a
        privacy right.
      </>
    ),
  },
  {
    heading: "Security",
    content: (
      <>
        We use reasonable administrative, technical, and organizational safeguards designed to protect
        information, including access controls, encrypted transport, restricted credentials, and
        monitoring. No system is completely secure. You are responsible for protecting your account
        and promptly reporting suspected unauthorized access.
      </>
    ),
  },
  {
    heading: "Changes to This Policy",
    content: "We may update this Policy as the Services or law changes. We will post the revised Policy with a new effective date and provide additional notice when required by law.",
  },
  {
    heading: "Contact Information",
    content: (
      <>
        Emerging Technology LLC<br />
        ShowMeLive Privacy Team<br />
        Jackson County, Missouri, United States<br />
        Privacy: <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:privacy@showmelive.app">privacy@showmelive.app</a><br />
        Support: <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:support@showmelive.app">support@showmelive.app</a><br />
        Legal and copyright: <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:legal@showmelive.app">legal@showmelive.app</a><br />
        Copyright complaints are governed by our{" "}
        <Link className="font-semibold text-blue-400 hover:text-blue-300" to="/copyright-dmca">Copyright/DMCA Policy</Link>.
      </>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalDocumentPage
    title="ShowMeLive Privacy Policy"
    sections={PRIVACY_SECTIONS}
    testId="privacy-policy-page"
  />
);

export default PrivacyPolicy;
